import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { createPortal } from 'react-dom';

/**
 * ImageCropModal - Minimalistisches Design wie Video-Generator
 */
export default function ImageCropModal({ imageSrc, onCropComplete, onCancel, aspectRatio = 9/16 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = useCallback((crop) => setCrop(crop), []);
  const onZoomChange = useCallback((zoom) => setZoom(zoom), []);
  const onCropCompleteInternal = useCallback((_, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels), []);

  const createCroppedImage = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    
    try {
      const image = new Image();
      if (!imageSrc.startsWith('data:')) image.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('Image load failed'));
        image.src = imageSrc;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const targetWidth = Math.min(1080, croppedAreaPixels.width);
      const targetHeight = Math.round(targetWidth / aspectRatio);
      
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.drawImage(
        image,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, targetWidth, targetHeight
      );

      const base64 = canvas.toDataURL('image/jpeg', 0.95);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Blob failed')), 'image/jpeg', 0.95);
      });
      
      console.log('Crop complete:', { blobSize: blob.size, width: targetWidth, height: targetHeight });
      // croppedAreaPixels im Callback mitgeben → Backend kann Original mit PIL croppen
      // (verlustfreier als Client-Canvas-Resize)
      onCropComplete({ base64, blob, width: targetWidth, height: targetHeight, croppedAreaPixels });
    } catch (error) {
      console.error('Crop error:', error);
      alert('Fehler beim Zuschneiden');
    } finally {
      setIsProcessing(false);
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[100000] bg-black flex flex-col"
      style={{ touchAction: 'none' }}
    >
      {/* Header - Minimalistisch */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm z-10">
        <button
          onClick={onCancel}
          className="text-white/50 hover:text-white text-sm font-light tracking-wide transition-colors"
        >
          Abbrechen
        </button>
        <span className="text-white/70 text-xs font-light tracking-wider uppercase">
          Ausschnitt
        </span>
        <button
          onClick={createCroppedImage}
          disabled={isProcessing}
          className="text-[#10B981] hover:text-[#34D399] text-sm font-normal tracking-wide disabled:opacity-40 transition-colors"
        >
          {isProcessing ? '...' : 'Fertig'}
        </button>
      </div>

      {/* Crop Area */}
      <div className="flex-1 relative">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropCompleteInternal}
          cropShape="rect"
          showGrid={true}
          style={{
            containerStyle: { background: '#000' },
            cropAreaStyle: { 
              border: '1px solid rgba(16, 185, 129, 0.6)',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)'
            }
          }}
        />
      </div>

      {/* Zoom Slider - Minimalistisch */}
      <div className="px-6 py-4 bg-black/60 backdrop-blur-sm">
        <div className="flex items-center gap-3 max-w-sm mx-auto">
          <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-[2px] bg-white/10 rounded-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-4
                       [&::-webkit-slider-thumb]:h-4
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-white
                       [&::-webkit-slider-thumb]:shadow-lg
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-moz-range-thumb]:w-4
                       [&::-moz-range-thumb]:h-4
                       [&::-moz-range-thumb]:rounded-full
                       [&::-moz-range-thumb]:bg-white
                       [&::-moz-range-thumb]:border-0
                       [&::-moz-range-thumb]:cursor-pointer"
          />
          <svg className="w-4 h-4 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
          </svg>
        </div>
      </div>
    </div>,
    document.body
  );
}
